import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  VStack,
  HStack,
  Text,
  Box,
  useColorModeValue,
  Divider,
  Button,
  Collapse,
} from '@chakra-ui/react'
import {
  ViewIcon,
  SettingsIcon,
  ChatIcon,
  AttachmentIcon,
  InfoIcon,
  EditIcon,
  ExternalLinkIcon,
  QuestionIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BellIcon,
} from '@chakra-ui/icons'
import { hasPermission, type Role } from '../config/permissions'
import axios from 'axios'

interface SidebarProps {
  onClose?: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ReactElement
  description: string
  permission: string
}

interface NavGroup {
  label: string
  icon: React.ReactElement
  items: NavItem[]
  defaultOpen?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [version, setVersion] = useState('1.3.1')
  const [isInitialized, setIsInitialized] = useState(false)
  
  // 그룹별 열림/닫힘 상태
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({
    logs: false,
    reports: false,
    boards: false,
    settings: false,
  })

  const bgColor = useColorModeValue('white', 'gray.800')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const activeBg = useColorModeValue('bexco.50', 'bexco.900')
  const activeColor = useColorModeValue('bexco.700', 'bexco.300')
  const groupHeaderBg = useColorModeValue('gray.100', 'gray.700')

  // 현재 사용자 역할 가져오기
  const userRole = (localStorage.getItem('userRole') || 'admin') as Role

  // 버전 정보 로드
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const response = await axios.get('/api/version')
        if (response.data.success) {
          setVersion(response.data.data.systemVersion)
        }
      } catch (error) {
        // 실패해도 기본값 유지
      }
    }
    loadVersion()
  }, [])

  // 초기 로드 시에만 현재 경로에 해당하는 그룹 열기
  useEffect(() => {
    if (isInitialized) return // 이미 초기화되었으면 무시
    
    const path = location.pathname
    const newOpenGroups = { ...openGroups }
    
    if (path.includes('/logs') || path.includes('/crawler-logs') || path.includes('/workflow-logs')) {
      newOpenGroups.logs = true
    }
    if (path.includes('/analysis-report') || path.includes('/validation-report')) {
      newOpenGroups.reports = true
    }
    if (path.includes('/announcements') || path.includes('/faqs') || path.includes('/inquiry-board')) {
      newOpenGroups.boards = true
    }
    if (path.includes('/files') || path.includes('/prompt-rules') || path.includes('/settings')) {
      newOpenGroups.settings = true
    }
    
    setOpenGroups(newOpenGroups)
    setIsInitialized(true)
  }, [location.pathname, isInitialized])

  const toggleGroup = (groupKey: string) => {
    setOpenGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
  }

  // 대시보드 (단독 메뉴)
  const dashboardItem: NavItem = {
    label: '대시보드',
    path: '/admin/dashboard',
    icon: <ViewIcon />,
    description: '시스템 현황 및 통계',
    permission: 'DASHBOARD_VIEW',
  }

  // 로그 그룹
  const logsGroup: NavGroup = {
    label: '로그',
    icon: <ChatIcon />,
    items: [
      {
        label: '쿼리 로그',
        path: '/admin/logs',
        icon: <ChatIcon />,
        description: '사용자 질의 로그 분석',
        permission: 'LOGS_VIEW',
      },
      {
        label: '크롤링 로그',
        path: '/admin/crawler-logs',
        icon: <ExternalLinkIcon />,
        description: '자동 크롤링 실행 로그',
        permission: 'LOGS_VIEW',
      },
      {
        label: '워크플로우 로그',
        path: '/admin/workflow-logs',
        icon: <ViewIcon />,
        description: '검색 워크플로우 추적',
        permission: 'WORKFLOW_LOGS_VIEW', // admin만
      },
    ],
  }

  // 리포트 그룹
  const reportsGroup: NavGroup = {
    label: '리포트',
    icon: <ViewIcon />,
    items: [
      {
        label: '분석 리포트',
        path: '/admin/analysis-report',
        icon: <ViewIcon />,
        description: '기간별 통계 분석',
        permission: 'ANALYSIS_REPORT_VIEW',
      },
      {
        label: '검증 리포트',
        path: '/admin/validation-report',
        icon: <ViewIcon />,
        description: 'RAG 응답 품질 검증',
        permission: 'DASHBOARD_VIEW',
      },
    ],
  }

  // 게시판 그룹
  const boardsGroup: NavGroup = {
    label: '게시판',
    icon: <InfoIcon />,
    items: [
      {
        label: '공지사항',
        path: '/admin/announcements',
        icon: <BellIcon />,
        description: '시스템 공지사항',
        permission: 'ANNOUNCEMENT_VIEW',
      },
      {
        label: '자주하는 질문',
        path: '/admin/faqs',
        icon: <InfoIcon />,
        description: 'FAQ 관리',
        permission: 'FAQ_VIEW',
      },
      {
        label: '문의게시판',
        path: '/admin/inquiry-board',
        icon: <QuestionIcon />,
        description: '문의 및 요청사항',
        permission: 'INQUIRY_BOARD_VIEW',
      },
    ],
  }

  // 설정 그룹
  const settingsGroup: NavGroup = {
    label: '설정',
    icon: <SettingsIcon />,
    items: [
      {
        label: '파일 관리',
        path: '/admin/files',
        icon: <AttachmentIcon />,
        description: '문서 및 데이터',
        permission: 'FILES_VIEW',
      },
      {
        label: '프롬프트 규칙',
        path: '/admin/prompt-rules',
        icon: <EditIcon />,
        description: 'AI 프롬프트 규칙',
        permission: 'PROMPT_RULES_VIEW',
      },
      {
        label: '설정',
        path: '/admin/settings',
        icon: <SettingsIcon />,
        description: '시스템 설정',
        permission: 'SETTINGS_EDIT', // admin만
      },
    ],
  }

  const handleNavClick = (path: string) => {
    navigate(path)
    if (onClose) {
      onClose()
    }
  }

  // 그룹 내 메뉴 필터링 (권한 체크)
  const filterGroupItems = (items: NavItem[]) => {
    return items.filter(item => hasPermission(userRole, item.permission as any))
  }

  // 메뉴 아이템 렌더링
  const renderNavItem = (item: NavItem, isSubItem = false) => {
    const isActive = location.pathname === item.path
    
    return (
      <Box
        key={item.path}
        p={isSubItem ? 2 : 3}
        pl={isSubItem ? 6 : 3}
        borderRadius="lg"
        cursor="pointer"
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : 'gray.600'}
        _hover={{
          bg: isActive ? activeBg : hoverBg,
          color: isActive ? activeColor : 'bexco.600',
        }}
        transition="all 0.2s"
        onClick={() => handleNavClick(item.path)}
      >
        <HStack spacing={3}>
          <Box fontSize={isSubItem ? 'md' : 'lg'} color={isActive ? 'bexco.500' : 'gray.500'}>
            {item.icon}
          </Box>
          <VStack align="start" spacing={0} flex="1">
            <Text fontSize="sm" fontWeight={isActive ? 'semibold' : 'medium'}>
              {item.label}
            </Text>
            {!isSubItem && (
              <Text fontSize="xs" color="gray.400">
                {item.description}
              </Text>
            )}
          </VStack>
        </HStack>
      </Box>
    )
  }

  // 그룹 헤더 렌더링
  const renderGroupHeader = (group: NavGroup, groupKey: string, filteredItems: NavItem[]) => {
    if (filteredItems.length === 0) return null
    
    const isOpen = openGroups[groupKey]
    const hasActiveItem = filteredItems.some(item => location.pathname === item.path)
    
    return (
      <Box key={groupKey}>
        <Box
          p={3}
          borderRadius="lg"
          cursor="pointer"
          _hover={{ bg: hoverBg }}
          transition="all 0.2s"
          onClick={() => toggleGroup(groupKey)}
        >
          <HStack spacing={3}>
            <Box fontSize="lg" color={hasActiveItem ? 'bexco.500' : 'gray.500'}>
              {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </Box>
            <Box fontSize="lg" color={hasActiveItem ? 'bexco.500' : 'gray.500'}>
              {group.icon}
            </Box>
            <Text 
              fontSize="sm" 
              fontWeight="semibold" 
              color={hasActiveItem ? 'bexco.600' : 'gray.700'}
            >
              {group.label}
            </Text>
          </HStack>
        </Box>
        
        <Collapse in={isOpen} animateOpacity>
          <VStack spacing={1} align="stretch" pl={2}>
            {filteredItems.map(item => renderNavItem(item, true))}
          </VStack>
        </Collapse>
      </Box>
    )
  }

  // 필터링된 그룹 아이템
  const filteredLogsItems = filterGroupItems(logsGroup.items)
  const filteredReportsItems = filterGroupItems(reportsGroup.items)
  const filteredBoardsItems = filterGroupItems(boardsGroup.items)
  const filteredSettingsItems = filterGroupItems(settingsGroup.items)

  return (
    <VStack
      h="full"
      bg={bgColor}
      spacing={0}
      align="stretch"
      p={4}
      overflow="hidden"
    >
      {/* BEXCO 로고 */}
      <Box p={4} textAlign="center" flexShrink={0}>
        <HStack justify="center" spacing={3}>
          <Box
            w="40px"
            h="40px"
            bg="bexco.500"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize="lg"
            fontWeight="bold"
          >
            B
          </Box>
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold" color="bexco.700">
              BEXCO
            </Text>
            <Text fontSize="xs" color="gray.500">
              AI 챗봇 관리
            </Text>
          </VStack>
        </HStack>
      </Box>

      <Divider flexShrink={0} />

      {/* 네비게이션 메뉴 */}
      <VStack 
        spacing={1} 
        align="stretch" 
        mt={4} 
        mb={4}
        flex="1" 
        overflowY="auto"
        overflowX="hidden"
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#A0AEC0',
          },
        }}
      >
        {/* 대시보드 (단독) */}
        {hasPermission(userRole, dashboardItem.permission as any) && renderNavItem(dashboardItem)}

        {/* 로그 그룹 */}
        {renderGroupHeader(logsGroup, 'logs', filteredLogsItems)}

        {/* 리포트 그룹 */}
        {renderGroupHeader(reportsGroup, 'reports', filteredReportsItems)}

        {/* 게시판 그룹 */}
        {renderGroupHeader(boardsGroup, 'boards', filteredBoardsItems)}

        {/* 설정 그룹 */}
        {renderGroupHeader(settingsGroup, 'settings', filteredSettingsItems)}
      </VStack>

      {/* 사용자가이드 버튼 */}
      <Box px={4} pb={3} flexShrink={0}>
        <Button
          w="full"
          colorScheme="bexco"
          variant="outline"
          size="md"
          leftIcon={<QuestionIcon />}
          onClick={() => handleNavClick('/admin/user-guide')}
          _hover={{
            bg: 'bexco.50',
            borderColor: 'bexco.500',
            color: 'bexco.700',
          }}
        >
          사용자가이드
        </Button>
      </Box>

      {/* 하단 정보 */}
      <Box p={4} pt={0} flexShrink={0}>
        <VStack spacing={2} align="center">
          <Text fontSize="xs" color="gray.400" textAlign="center">
            BEXCO 지능형 공간 안내 시스템
          </Text>
          <HStack spacing={2} fontSize="xs" color="gray.400">
            <Text>v{version}</Text>
            <Text>•</Text>
            <Text>Admin</Text>
          </HStack>
        </VStack>
      </Box>
    </VStack>
  )
}

export default Sidebar
